/**
 * PermissionGrid Component
 * Matrix view for editing role permissions with efficient rendering
 *
 * Features:
 * - Roles as columns, permissions as rows (grouped by category)
 * - Categories collapsed by default showing aggregate counts
 * - Checkbox at each intersection for permission assignment
 * - Batch save for performance
 * - Memoized cells to handle large grids efficiently
 */

import React, { useState, useMemo, useCallback, memo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { colors, tokenVars } from '../../theme';

// Spacing values (in pixels)
const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};
import { PermissionGridCell } from './PermissionGridCell';
import { PermissionGridHeader, type RoleColumnInfo } from './PermissionGridHeader';
import { PermissionGridCategoryRow, type CategoryAggregates } from './PermissionGridCategoryRow';
import { LockedBadge, LockedCallout, LockedReason, lockedBand } from '../base/Locked';
import { permissionLockBadge, permissionLockHelp, permissionLockText, type PermissionLockReasons } from './permissionLocks';

export interface PermissionInfo {
  id: string;
  key: string;
  label: string;
  category: string;
  sortOrder?: number;
  scope?: string;
}

export interface RolePermissionChange {
  roleId: string;
  addPermissions?: string[];
  removePermissions?: string[];
}

export interface PermissionGridProps {
  /** Role type being edited: 'account' or 'location' */
  roleType: 'account' | 'location';
  /** Roles to display as columns */
  roles: RoleColumnInfo[];
  /** Permissions to display as rows (filtered by role type) */
  permissions: PermissionInfo[];
  /** Current permission matrix: roleId -> Set<permissionKey> */
  matrix: Map<string, Set<string>>;
  /** Callback when save is clicked with all changes */
  onSave: (changes: RolePermissionChange[]) => Promise<void>;
  /** Callback to create a new role */
  onRoleCreate?: () => void;
  /** Callback to edit a role */
  onRoleEdit?: (roleId: string) => void;
  /** Callback to disable a role (soft delete) */
  onRoleDisable?: (roleId: string) => void;
  /** Callback to enable a disabled role (undelete) */
  onRoleEnable?: (roleId: string) => void;
  /** Whether the grid is editable */
  canEdit?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Maximum height for the grid */
  maxHeight?: string;
  /** Search/filter term */
  searchTerm?: string;
  /**
   * When true, the inline "Discard" + "Save Changes" buttons and the
   * "Unsaved changes." footer text are hidden. The parent is expected
   * to read dirty state via `onDirtyChange` and call `save()` on the
   * exposed ref. Used by the AccountModal so the role grid saves
   * through the modal's "Update Account" footer button instead of a
   * secondary control.
   */
  hideInlineSaveControls?: boolean;
  /**
   * Fires whenever the grid's dirty state flips, so a parent can mirror
   * the flag onto its own TabSaveHandle without consulting the ref.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * Permissions the current user cannot give. A role that does not already
   * have one shows it greyed out and not tickable; a role that already has it
   * (when the grid loaded) can untick and re-tick it.
   */
  lockedKeys?: string[];
  /** Why each locked key is locked; shown under the permission's name. */
  lockedReasons?: PermissionLockReasons;
}

/**
 * Imperative handle the grid exposes to a parent ref. Mirrors the
 * TabSaveHandle pattern used elsewhere in the account modal so the
 * "Update Account" footer button can flush role-permission edits the
 * same way it flushes timesheets / billing / invoicing.
 */
export interface PermissionGridHandle {
  isDirty: boolean;
  save: () => Promise<void>;
}

interface CategoryGroup {
  category: string;
  permissions: PermissionInfo[];
}

function sameKeys(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

/**
 * Groups permissions by category and sorts them
 */
function groupPermissionsByCategory(permissions: PermissionInfo[]): CategoryGroup[] {
  const groups = new Map<string, PermissionInfo[]>();

  permissions.forEach((perm) => {
    const category = perm.category || 'Other';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(perm);
  });

  // Sort categories alphabetically, but put 'Other' at the end
  const sortedCategories = Array.from(groups.keys()).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  return sortedCategories.map((category) => ({
    category,
    permissions: groups.get(category)!.sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      return a.label.localeCompare(b.label);
    }),
  }));
}

/**
 * PermissionGrid - Matrix view for editing role permissions
 */
const PermissionGridImpl = forwardRef<PermissionGridHandle, PermissionGridProps>(function PermissionGrid(
  {
    roleType,
    roles,
    permissions,
    matrix,
    onSave,
    onRoleCreate,
    onRoleEdit,
    onRoleDisable,
    onRoleEnable,
    canEdit = true,
    loading = false,
    error = null,
    maxHeight = '70vh',
    searchTerm = '',
    hideInlineSaveControls = false,
    onDirtyChange,
    lockedKeys,
    lockedReasons,
  },
  ref
) {
  // Locks only matter to someone who can change the grid.
  const lockedSet = useMemo(() => new Set(canEdit ? lockedKeys || [] : []), [lockedKeys, canEdit]);
  const lockIdPrefix = React.useId();
  // The locked cell whose reason was opened by a tap (one at a time).
  const [revealed, setRevealed] = useState<{ roleId: string; key: string } | null>(null);
  const handleLockedPress = useCallback((roleId: string, key: string) => {
    setRevealed((r) => (r && r.roleId === roleId && r.key === key ? null : { roleId, key }));
  }, []);
  // An open reason belongs to the tap that opened it: a new search, other
  // roles or new locks close it (re-showing it later would steal focus).
  const roleIdsKey = roles.map((r) => r.id).join(',');
  const lockedKeysKey = [...lockedSet].join(',');
  useEffect(() => setRevealed(null), [searchTerm, roleIdsKey, lockedKeysKey]);

  // Track local changes (not yet saved)
  const [localMatrix, setLocalMatrix] = useState<Map<string, Set<string>>>(matrix);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset local matrix when props change. A role whose saved permissions did
  // not change keeps its unsaved edits: after a save that went through for
  // some roles only, the reload must not wipe the edits of the roles that
  // were refused (the message asks the user to fix those).
  const prevMatrixRef = React.useRef(matrix);
  React.useEffect(() => {
    const prev = prevMatrixRef.current;
    prevMatrixRef.current = matrix;
    setLocalMatrix((local) => {
      const next = new Map(matrix);
      if (prev === matrix) return next;
      for (const [roleId, saved] of matrix) {
        const before = prev.get(roleId);
        const edited = local.get(roleId);
        if (before && edited && sameKeys(before, saved)) next.set(roleId, edited);
      }
      return next;
    });
  }, [matrix]);

  // Filter permissions by search term
  const filteredPermissions = useMemo(() => {
    if (!searchTerm.trim()) return permissions;
    const term = searchTerm.toLowerCase();
    return permissions.filter(
      (p) => p.label.toLowerCase().includes(term) || p.key.toLowerCase().includes(term) || (p.category || '').toLowerCase().includes(term)
    );
  }, [permissions, searchTerm]);

  // Group permissions by category
  const categoryGroups = useMemo(() => groupPermissionsByCategory(filteredPermissions), [filteredPermissions]);

  // Calculate category aggregates
  const categoryAggregates = useMemo(() => {
    const aggregates = new Map<string, CategoryAggregates>();

    categoryGroups.forEach((group) => {
      const perRole: Record<string, number> = {};

      roles.forEach((role) => {
        const rolePerms = localMatrix.get(role.id) || new Set();
        const count = group.permissions.filter((p) => rolePerms.has(p.key)).length;
        perRole[role.id] = count;
      });

      aggregates.set(group.category, {
        total: group.permissions.length,
        perRole,
        // Locked rows that some editable role could not tick (not every role has it).
        locked: group.permissions.filter((p) => lockedSet.has(p.key) && roles.some((r) => !r.isSystem && !matrix.get(r.id)?.has(p.key)))
          .length,
      });
    });

    return aggregates;
  }, [categoryGroups, roles, localMatrix, lockedSet, matrix]);

  // Check if there are pending changes
  const hasChanges = useMemo(() => {
    for (const role of roles) {
      const original = matrix.get(role.id) || new Set();
      const local = localMatrix.get(role.id) || new Set();

      if (original.size !== local.size) return true;

      for (const key of local) {
        if (!original.has(key)) return true;
      }
      for (const key of original) {
        if (!local.has(key)) return true;
      }
    }
    return false;
  }, [roles, matrix, localMatrix]);

  // Handle cell change
  const handleCellChange = useCallback((roleId: string, permissionKey: string, checked: boolean) => {
    setLocalMatrix((prev) => {
      const next = new Map(prev);
      const rolePerms = new Set(next.get(roleId) || []);

      if (checked) {
        rolePerms.add(permissionKey);
      } else {
        rolePerms.delete(permissionKey);
      }

      next.set(roleId, rolePerms);
      return next;
    });
    setSaveError(null);
  }, []);

  // Handle category toggle
  const handleCategoryToggle = useCallback((category: string) => {
    setRevealed(null);
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Check if all categories are expanded
  const allExpanded = useMemo(() => {
    return categoryGroups.length > 0 && categoryGroups.every((g) => expandedCategories.has(g.category));
  }, [categoryGroups, expandedCategories]);

  // Handle expand/collapse all
  const handleToggleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExpandedCategories(new Set());
    } else {
      setExpandedCategories(new Set(categoryGroups.map((g) => g.category)));
    }
  }, [allExpanded, categoryGroups]);

  // Handle save
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    try {
      // Calculate changes for each role
      const changes: RolePermissionChange[] = [];

      roles.forEach((role) => {
        const original = matrix.get(role.id) || new Set();
        const local = localMatrix.get(role.id) || new Set();

        const addPermissions: string[] = [];
        const removePermissions: string[] = [];

        // Find additions
        for (const key of local) {
          if (!original.has(key)) {
            addPermissions.push(key);
          }
        }

        // Find removals
        for (const key of original) {
          if (!local.has(key)) {
            removePermissions.push(key);
          }
        }

        if (addPermissions.length > 0 || removePermissions.length > 0) {
          changes.push({
            roleId: role.id,
            addPermissions: addPermissions.length > 0 ? addPermissions : undefined,
            removePermissions: removePermissions.length > 0 ? removePermissions : undefined,
          });
        }
      });

      if (changes.length > 0) {
        await onSave(changes);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      // Re-raised so a parent save bar (hideInlineSaveControls) reports the
      // failure instead of "saved"; the inline button catches it below.
      throw err;
    } finally {
      setSaving(false);
    }
  }, [roles, matrix, localMatrix, onSave]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    setLocalMatrix(new Map(matrix));
    setSaveError(null);
  }, [matrix]);

  // Notify parent on dirty-flag transitions so it can mirror the state onto
  // its own TabSaveHandle. Effect (not derived) because consumers track
  // isDirty in their own React state.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(hasChanges);
  }, [hasChanges, onDirtyChange]);

  // Expose isDirty + save to a parent ref so the account modal's
  // "Update Account" footer button can flush role changes alongside the
  // other tabs' edits (timesheets, invoicing, billing).
  useImperativeHandle(
    ref,
    () => ({
      isDirty: hasChanges,
      save: handleSave,
    }),
    [hasChanges, handleSave]
  );

  // Check if a permission is enabled for a role
  const isPermissionEnabled = useCallback(
    (roleId: string, permissionKey: string): boolean => {
      return localMatrix.get(roleId)?.has(permissionKey) || false;
    },
    [localMatrix]
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: SPACING.xl,
          color: colors.text.secondary,
        }}
      >
        Loading permissions...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: SPACING.lg,
          color: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '8px',
          textAlign: 'center',
        }}
      >
        {error}
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div
        style={{
          padding: SPACING.xl,
          textAlign: 'center',
          color: colors.text.secondary,
        }}
      >
        <p>No {roleType} roles found.</p>
        {canEdit && onRoleCreate && (
          <button
            onClick={onRoleCreate}
            style={{
              marginTop: SPACING.md,
              padding: '8px 16px',
              backgroundColor: colors.brand.primary,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Create Role
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: SPACING.sm,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
          <span style={{ fontSize: '12px', color: colors.text.secondary }}>
            {roleType === 'location' ? 'Location' : 'Account'} Roles ({roles.length})
          </span>
          {categoryGroups.length > 0 && (
            <button
              onClick={handleToggleExpandAll}
              style={{
                padding: '4px 10px',
                backgroundColor: 'transparent',
                color: colors.text.secondary,
                border: `1px solid ${colors.border.default}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.15s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = colors.background.secondary;
                e.currentTarget.style.color = colors.text.primary;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = colors.text.secondary;
              }}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: SPACING.sm }}>
          {canEdit && onRoleCreate && (
            <button
              onClick={onRoleCreate}
              style={{
                padding: '6px 12px',
                backgroundColor: 'transparent',
                color: colors.brand.primary,
                border: `1px solid ${colors.brand.primary}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.15s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = colors.brand.primary;
                e.currentTarget.style.color = '#fff';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = colors.brand.primary;
              }}
            >
              + Add Role
            </button>
          )}

          {hasChanges && !hideInlineSaveControls && (
            <>
              <button
                onClick={handleDiscard}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: colors.text.secondary,
                  border: `1px solid ${colors.border.default}`,
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Discard
              </button>
              <button
                onClick={() => {
                  handleSave().catch(() => undefined); // shown as saveError
                }}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#22c55e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save error message */}
      {saveError && (
        <div
          role="alert"
          data-testid="permission-grid-save-error"
          style={{
            whiteSpace: 'pre-line',
            padding: SPACING.sm,
            color: '#ef4444',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '6px',
            fontSize: '12px',
          }}
        >
          {saveError}
        </div>
      )}

      {/* Grid container */}
      <div
        style={{
          maxHeight,
          overflow: 'auto',
          borderRadius: '8px',
          border: `1px solid ${colors.border.default}`,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
          }}
        >
          <thead>
            {/* Role names row */}
            <tr>
              {/* Permission column header (sticky left) */}
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  backgroundColor: colors.background.secondary,
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: `1px solid ${colors.border.default}`,
                  borderRight: `1px solid ${colors.border.default}`,
                  minWidth: '200px',
                  zIndex: 3,
                  color: colors.text.primary,
                  fontWeight: 600,
                }}
              >
                Permission
              </th>

              {/* Role column headers */}
              {roles.map((role) => (
                <PermissionGridHeader key={role.id} role={role} onEdit={canEdit ? onRoleEdit : undefined} canEdit={canEdit} />
              ))}
            </tr>

            {/* Actions row */}
            {canEdit && (onRoleDisable || onRoleEnable) && (
              <tr>
                <th
                  style={{
                    position: 'sticky',
                    top: '120px',
                    left: 0,
                    backgroundColor: colors.background.tertiary || '#1a1a1a',
                    padding: '6px 12px',
                    textAlign: 'left',
                    borderBottom: `2px solid ${colors.border.default}`,
                    borderRight: `1px solid ${colors.border.default}`,
                    minWidth: '200px',
                    zIndex: 3,
                    color: colors.text.secondary,
                    fontSize: '11px',
                  }}
                >
                  Actions
                </th>
                {roles.map((role) => (
                  <th
                    key={`action-${role.id}`}
                    style={{
                      position: 'sticky',
                      top: '120px',
                      backgroundColor: colors.background.tertiary || '#1a1a1a',
                      padding: '6px 2px',
                      textAlign: 'center',
                      borderBottom: `2px solid ${colors.border.default}`,
                      borderRight: `1px solid ${colors.border.default}`,
                      zIndex: 2,
                    }}
                  >
                    {!role.isSystem && role.isDeleted && onRoleEnable && (
                      <button
                        onClick={() => onRoleEnable(role.id)}
                        style={{
                          background: 'rgba(34, 197, 94, 0.08)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          cursor: 'pointer',
                          padding: '3px 8px',
                          fontSize: '10px',
                          color: '#22c55e',
                          borderRadius: '4px',
                          transition: 'all 0.15s ease',
                          fontWeight: 500,
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
                          e.currentTarget.style.borderColor = '#22c55e';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)';
                        }}
                        title={`Enable ${role.name}`}
                      >
                        Enable
                      </button>
                    )}
                    {!role.isSystem && !role.isDeleted && onRoleDisable && (
                      <button
                        onClick={() => onRoleDisable(role.id)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.08)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          cursor: 'pointer',
                          padding: '3px 8px',
                          fontSize: '10px',
                          color: '#ef4444',
                          borderRadius: '4px',
                          transition: 'all 0.15s ease',
                          fontWeight: 500,
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                          e.currentTarget.style.borderColor = '#ef4444';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                        }}
                        title={`Disable ${role.name}`}
                      >
                        Disable
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {categoryGroups.map((group) => {
              const isExpanded = expandedCategories.has(group.category);
              const aggregates = categoryAggregates.get(group.category)!;

              return (
                <React.Fragment key={group.category}>
                  {/* Category header row */}
                  <PermissionGridCategoryRow
                    category={group.category}
                    aggregates={aggregates}
                    roleIds={roles.map((r) => r.id)}
                    isExpanded={isExpanded}
                    onToggle={() => handleCategoryToggle(group.category)}
                  />

                  {/* Permission rows (only when expanded) */}
                  {isExpanded &&
                    group.permissions.map((permission) => {
                      const rowLocked = lockedSet.has(permission.key);
                      const reason = lockedReasons?.[permission.key];
                      const reasonId = `${lockIdPrefix}-${permission.key}`;
                      const badge = permissionLockBadge(reason);
                      const reasonText = permissionLockText(reason);
                      const openRole =
                        rowLocked && revealed?.key === permission.key
                          ? roles.find((r) => r.id === revealed.roleId && !r.isSystem && !matrix.get(r.id)?.has(permission.key))
                          : undefined;
                      return (
                        <React.Fragment key={permission.key}>
                          <tr data-locked={rowLocked || undefined}>
                            {/* Permission label cell (sticky left) */}
                            <td
                              style={{
                                position: 'sticky',
                                left: 0,
                                backgroundColor: colors.background.primary,
                                padding: '8px 12px 8px 36px',
                                borderBottom: `1px solid ${colors.border.default}`,
                                borderRight: `1px solid ${colors.border.default}`,
                                color: colors.text.primary,
                                fontSize: '12px',
                                zIndex: 1,
                                textAlign: 'left',
                                ...(rowLocked
                                  ? { ...lockedBand(colors.background.primary), boxShadow: `inset 4px 0 0 ${tokenVars.warning}` }
                                  : {}),
                              }}
                              title={permission.key}
                            >
                              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                                <span>{permission.label}</span>
                                {rowLocked && <LockedBadge text={badge} testId="permission-locked-badge" />}
                              </span>
                              {rowLocked && (
                                <LockedReason
                                  id={reasonId}
                                  testId="permission-locked-reason"
                                  style={{ marginTop: 4, whiteSpace: 'normal' }}
                                >
                                  {reasonText}
                                </LockedReason>
                              )}
                            </td>

                            {/* Checkbox cells for each role */}
                            {roles.map((role) => {
                              const savedOnRole = !!matrix.get(role.id)?.has(permission.key);
                              // System roles can't be edited anyway: plain disabled checkbox.
                              const cellLocked = rowLocked && !savedOnRole && !role.isSystem;
                              return (
                                <PermissionGridCell
                                  key={`${role.id}-${permission.key}`}
                                  roleId={role.id}
                                  permissionKey={permission.key}
                                  isChecked={isPermissionEnabled(role.id, permission.key)}
                                  isDisabled={!canEdit || role.isSystem}
                                  isLocked={cellLocked}
                                  isKept={rowLocked && savedOnRole}
                                  lockedLabel={`${role.name}: ${permission.label}. ${badge}. ${reasonText}`}
                                  lockedExpanded={cellLocked && openRole?.id === role.id}
                                  onLockedPress={handleLockedPress}
                                  ariaLabel={`${role.name}: ${permission.label}`}
                                  describedBy={
                                    rowLocked ? (cellLocked && openRole?.id === role.id ? `${reasonId}-open` : reasonId) : undefined
                                  }
                                  onChange={handleCellChange}
                                />
                              );
                            })}
                          </tr>
                          {openRole && (
                            <tr data-testid="permission-locked-callout-row">
                              <td colSpan={roles.length + 1} style={{ padding: 6, borderBottom: `1px solid ${colors.border.default}` }}>
                                <LockedCallout
                                  id={`${reasonId}-open`}
                                  testId="permission-locked-callout"
                                  title={`${openRole.name}: ${permission.label}`}
                                  onClose={() => setRevealed(null)}
                                  returnFocus={() =>
                                    document.querySelector<HTMLElement>(
                                      `[data-testid="permission-locked-cell-${openRole.id}-${permission.key}"]`
                                    )
                                  }
                                  style={{ position: 'sticky', left: 6, maxWidth: 'min(560px, calc(100vw - 80px))' }}
                                >
                                  {badge}. {reasonText} {permissionLockHelp(reason)}
                                </LockedCallout>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info text. When the parent is handling save (hideInlineSaveControls),
          the "Unsaved changes." copy would be misleading — the parent's
          footer button is the actual save UX. Show only the help text. */}
      <div
        style={{
          fontSize: '11px',
          color: colors.text.tertiary,
          textAlign: 'center',
        }}
      >
        {hasChanges && !hideInlineSaveControls ? 'Unsaved changes.' : 'Click category row to expand/collapse.'}
      </div>
    </div>
  );
});

export const PermissionGrid = memo(PermissionGridImpl) as typeof PermissionGridImpl;

export default PermissionGrid;
